import React, { useEffect } from 'react';
import { Grid, Button } from '@mui/material';
import SecretField from 'components/JobList/CreateJob/SecretField';
import { useModalContext } from './ModalContext';
import {
  SectionCard,
  useIntegrationConfig,
  useIntegrationSource,
  IntegrationDetailsCard,
  AuthenticationCard,
  EmployeeIdCounterCard,
  EmailNotificationsCard
} from './greenhouseFormShared';

// charlotte/src/handleNotifications/buildEmailData.ts reads config.emailConfig.* without
// optional chaining for Greenhouse integrations - a missing emailConfig throws at runtime
// when a hire notification is sent. Backfill it (and the other config sections) once, on
// mount, without clobbering anything already present (e.g. when editing an existing integration).
// authentication.isAdmin is always forced to true - it's not a user-facing choice.
//
// source.type is always forced to "webhook": Greenhouse Recruiting integrations are driven by
// Greenhouse webhooks, and charlotte-webhook only resolves an integration when
// source.type === "webhook" (see charlotte-webhook/service/mongo.js findWebhookIntegration).
// Without it every webhook (ping included) 404s.
const useIntegrationDefaults = () => {
  const { formState, setFormState } = useModalContext();
  useEffect(() => {
    const rawConfig = formState.values.config;
    const config = rawConfig ? JSON.parse(rawConfig) : {};
    const configWithDefaults = {
      lastId: 0,
      ...config,
      authentication: {
        ...config.authentication,
        isAdmin: true
      },
      emailConfig: {
        emailContacts: [],
        sendSuccessEmails: 'true',
        greenhouseEmployeeUrl: '',
        ...config.emailConfig
      }
    };

    const rawSource = formState.values.source;
    const source = rawSource ? JSON.parse(rawSource) : {};
    const sourceWithDefaults = {
      ...source,
      type: 'webhook'
    };

    setFormState({
      ...formState,
      values: {
        ...formState.values,
        config: JSON.stringify(configWithDefaults, null, 2),
        source: JSON.stringify(sourceWithDefaults, null, 2)
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

const GreenhouseApiKeyCard = () => {
  const [greenhouseApiKey, setGreenhouseApiKey] = useIntegrationConfig([
    'greenhouseApiKey'
  ]);
  return (
    <SectionCard
      subheader="Optional - reserved for upcoming functionality that calls the Greenhouse API directly"
      title="Greenhouse API key"
    >
      <SecretField
        label="API key"
        required={false}
        secret={greenhouseApiKey ?? ''}
        setState={setGreenhouseApiKey}
      />
    </SectionCard>
  );
};

const WebhookSecretCard = () => {
  const [secretKey, setSecretKey] = useIntegrationSource(['secretKey']);
  return (
    <SectionCard
      subheader="Must match the secret configured in Greenhouse's webhook settings"
      title="Webhook secret"
    >
      <SecretField
        label="Secret key"
        secret={secretKey ?? ''}
        setState={setSecretKey}
      />
    </SectionCard>
  );
};

export const GreenhouseApplicationForm = () => {
  const { onSubmitIntegration, isUpdate } = useModalContext();
  useIntegrationDefaults();

  return (
    <form onSubmit={onSubmitIntegration}>
      <Grid container direction="column" spacing={3} wrap="nowrap">
        <Grid item>
          <IntegrationDetailsCard />
        </Grid>
        <Grid item>
          <AuthenticationCard />
        </Grid>
        <Grid item>
          <GreenhouseApiKeyCard />
        </Grid>
        <Grid item>
          <EmployeeIdCounterCard />
        </Grid>
        <Grid item>
          <EmailNotificationsCard />
        </Grid>
        <Grid item>
          <WebhookSecretCard />
        </Grid>
        <Grid item>
          <Button color="primary" type="submit" variant="contained">
            {isUpdate ? 'Update Integration' : 'Save Integration'}
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default GreenhouseApplicationForm;
